import React from 'react';
import {useTranslation} from 'react-i18next';

import {skipToken} from '@reduxjs/toolkit/query';
import {addMonths} from 'date-fns/addMonths';
import {endOfDay} from 'date-fns/endOfDay';
import {endOfMonth} from 'date-fns/endOfMonth';
import {endOfQuarter} from 'date-fns/endOfQuarter';
import {getQuarter} from 'date-fns/getQuarter';
import {isSameDay} from 'date-fns/isSameDay';
import {isSameMonth} from 'date-fns/isSameMonth';
import {isSameQuarter} from 'date-fns/isSameQuarter';
import {isValid} from 'date-fns/isValid';
import {max as maxDate} from 'date-fns/max';
import {startOfDay} from 'date-fns/startOfDay';
import {startOfMonth} from 'date-fns/startOfMonth';
import {startOfQuarter} from 'date-fns/startOfQuarter';
import {subQuarters} from 'date-fns/subQuarters';
import {TFunction} from 'i18next';

import {TPeriodPickerOption, TPeriodPickerValue} from '@edna/components';
import {getPeriod} from '@edna/utils';

import {FilterField} from 'src/components';
import {
  EAgentClientAccountingPeriod,
  useGetAgentClientQuery,
} from 'src/containers/LeadCompanyClients';

import {useGetAgentUserQuery} from './api';
import * as S from './style';

const MIN_ACCOUNTING_DATE = new Date(2025, 0, 1);
const NOW = startOfMonth(new Date());
const START_ACCOUNTING_DATE = maxDate([MIN_ACCOUNTING_DATE, subQuarters(NOW, 5)]);

export const getAccountingPeriodValueByDate = (
  baseDate: Date | null,
  type: EAgentClientAccountingPeriod,
): TPeriodPickerValue => {
  const date = isValid(baseDate) ? (baseDate as Date) : new Date();

  if (type === EAgentClientAccountingPeriod.MONTH) {
    return {period: 'range', startDate: startOfMonth(date), stopDate: endOfMonth(date)};
  }

  return {period: 'range', startDate: startOfQuarter(date), stopDate: endOfQuarter(date)};
};

const getPeriodOptionByDate = (
  date: Date,
  type: EAgentClientAccountingPeriod,
  t: TFunction,
  labelPrefix: string,
): TPeriodPickerOption => {
  const value = getAccountingPeriodValueByDate(date, type);

  if (type === EAgentClientAccountingPeriod.MONTH) {
    return {
      label: `${t(`Common:monthsKeysByIndex.${date.getMonth()}`)} ${date.getFullYear()}`,
      value,
    };
  }

  const quarter = t(`${labelPrefix}.quarter`);

  return {label: `${getQuarter(date)} ${quarter} ${date.getFullYear()}`, value};
};

const getPeriodOptions = (type: EAgentClientAccountingPeriod) => {
  const options = [];

  let optionDate = START_ACCOUNTING_DATE;

  if (type === EAgentClientAccountingPeriod.QUARTER) {
    while (optionDate <= NOW || isSameQuarter(optionDate, NOW)) {
      options.unshift(optionDate);
      optionDate = addMonths(optionDate, 3);
    }

    return options;
  }

  while (optionDate <= NOW || isSameMonth(optionDate, NOW)) {
    options.unshift(optionDate);
    optionDate = addMonths(optionDate, 1);
  }

  return options;
};

const getInitialByPeriod = (
  type: EAgentClientAccountingPeriod,
  period?: TPeriodPickerValue | null,
): [TPeriodPickerValue, Date | null] => {
  if (!period || !period.stopDate || !period.startDate) {
    return [getAccountingPeriodValueByDate(new Date(), type), null];
  }

  const startDate = new Date(period.startDate);
  const stopDate = new Date(period.stopDate);

  const isOneDayPeriod = isSameDay(startDate, stopDate);

  if (isOneDayPeriod) {
    return [getAccountingPeriodValueByDate(startDate, type), startDate];
  }

  const initialPeriod = {period: 'range', startDate, stopDate} as TPeriodPickerValue;

  return [initialPeriod, null];
};

const getOneDayPeriod = (value?: Date | null) =>
  value
    ? ({
        period: 'range',
        startDate: startOfDay(value),
        stopDate: endOfDay(value),
      } as TPeriodPickerValue)
    : null;

type TAccountingPeriodFiltersProps = {
  labelPrefix: string;
  onChange: (value: TPeriodPickerValue | null) => void;
  initialValue?: TPeriodPickerValue | null;
  agentId?: string;
  hasAllTime?: boolean;
};

const AccountingPeriodFilters = React.memo<TAccountingPeriodFiltersProps>(
  ({labelPrefix, onChange, initialValue, agentId, hasAllTime}) => {
    const {t} = useTranslation();

    const {data: agentClient} = useGetAgentClientQuery(agentId ? {tenantId: agentId} : skipToken);
    const {data: agentUser} = useGetAgentUserQuery(undefined, {skip: !!agentId});

    const accountingPeriod = agentUser?.accountingPeriod || agentClient?.accountingPeriod;

    const [period, setPeriod] = React.useState<TPeriodPickerValue | null>(null);
    const [date, setDate] = React.useState<Date | null>(null);

    React.useEffect(() => {
      if (hasAllTime) {
        setPeriod(getPeriod('anytime'));
        setDate(null);

        return;
      }

      if (!accountingPeriod) {
        return;
      }

      const [initialPeriod, initialDate] = getInitialByPeriod(accountingPeriod, initialValue);

      setPeriod(initialPeriod);
      setDate(initialDate);

      onChange(getOneDayPeriod(initialDate) || initialPeriod);
    }, [accountingPeriod]);

    const handlePeriodChange = React.useCallback(
      (value: TPeriodPickerValue) => {
        setPeriod(value);
        setDate(null);

        onChange(value);
      },
      [onChange],
    );

    const handleDateChange = React.useCallback(
      (value?: unknown) => {
        if (!accountingPeriod) {
          return;
        }

        if (value instanceof Date) {
          setPeriod(getAccountingPeriodValueByDate(value, accountingPeriod));
          setDate(value);

          onChange(getOneDayPeriod(value));

          return;
        }

        const initialPeriod = getAccountingPeriodValueByDate(new Date(), accountingPeriod);

        setDate(null);
        setPeriod(initialPeriod);
        onChange(initialPeriod);
      },
      [accountingPeriod, onChange],
    );

    const periodOptions = React.useMemo<TPeriodPickerOption[]>(() => {
      if (!accountingPeriod) {
        return [];
      }

      const options = getPeriodOptions(accountingPeriod).map((dateOption) =>
        getPeriodOptionByDate(dateOption, accountingPeriod, t, labelPrefix),
      );

      if (hasAllTime) {
        return [{value: 'anytime', label: 'anytime'}, ...options];
      }

      return options;
    }, [accountingPeriod, t, labelPrefix, hasAllTime]);

    return (
      <>
        <FilterField label={t(`${labelPrefix}.period`)}>
          <S.PeriodPicker
            options={periodOptions}
            onChange={handlePeriodChange}
            value={period}
            showHeaderDate
            needForceClosing
          />
        </FilterField>
        <FilterField label={t(`${labelPrefix}.date`)}>
          <S.DatePicker value={date} onChange={handleDateChange} minDate={MIN_ACCOUNTING_DATE} />
        </FilterField>
      </>
    );
  },
);

AccountingPeriodFilters.displayName = 'AccountingPeriodFilters';

export {AccountingPeriodFilters};
